// mission-secrets-test-env-safe-file-read.ts — review 04a M1 (spec V2 §4.2).
//
// Getter known-values chạy trong MỌI `MissionStore.commit()` ⇒ đọc `test.env`
// không bao giờ được treo, đi theo symlink hay nuốt file khổng lồ (worker cùng
// uid có thể `mkfifo`/`ln -s /dev/zero` ở đúng đường dẫn):
//  - thư mục: `lstat` (không theo symlink) phải là thư mục thật;
//  - file: `open(O_RDONLY | O_NOFOLLOW | O_NONBLOCK)` — symlink ⇒ ELOOP, FIFO
//    mở không chặn; rồi `fstat` TRÊN CHÍNH FD: phải là file thường, ≤ 64 KiB;
//    đọc + kiểm quyền đều trên fd đó (không resolve lại đường dẫn — TOCTOU).
// Không đọc ⇒ không nạp gì vào registry.
import { constants, promises as fs, type Stats } from "node:fs";

export const TEST_ENV_MAX_BYTES = 64 * 1024;

export type TestEnvSafeReadResult =
  | { kind: "absent" }
  | { kind: "rejected"; problems: string[] }
  | { kind: "error"; code: string }
  | { kind: "read"; text: string; dir: Stats; file: Stats };

function errorCode(err: unknown): string {
  return (err as NodeJS.ErrnoException)?.code ?? "UNKNOWN";
}

export async function readTestEnvFileSafely(
  dirPath: string,
  filePath: string,
): Promise<TestEnvSafeReadResult> {
  let dir: Stats;
  try {
    dir = await fs.lstat(dirPath);
  } catch (err) {
    return errorCode(err) === "ENOENT"
      ? { kind: "absent" }
      : { kind: "error", code: errorCode(err) };
  }
  if (!dir.isDirectory()) {
    return { kind: "rejected", problems: [`${dirPath} không phải thư mục thật (symlink?)`] };
  }
  let handle: fs.FileHandle;
  try {
    handle = await fs.open(
      filePath,
      constants.O_RDONLY | constants.O_NOFOLLOW | constants.O_NONBLOCK,
    );
  } catch (err) {
    const code = errorCode(err);
    if (code === "ENOENT") return { kind: "absent" };
    if (code === "ELOOP")
      return { kind: "rejected", problems: [`${filePath} là symlink — không theo`] };
    return { kind: "error", code };
  }
  try {
    const file = await handle.stat();
    if (!file.isFile()) {
      return {
        kind: "rejected",
        problems: [`${filePath} không phải file thường (FIFO/thiết bị?)`],
      };
    }
    if (file.size > TEST_ENV_MAX_BYTES) return { kind: "error", code: "FILE_TOO_LARGE" };
    const buffer = await handle.readFile();
    if (buffer.length > TEST_ENV_MAX_BYTES) return { kind: "error", code: "FILE_TOO_LARGE" };
    return { kind: "read", text: buffer.toString("utf8"), dir, file };
  } catch (err) {
    return { kind: "error", code: errorCode(err) };
  } finally {
    await handle.close().catch(() => {});
  }
}
